import React, { useState, useCallback, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Box, FormHelperText, Typography } from '@mui/material';
import { StyledPhoneInputContainer } from '../../../core/components/ui/PhoneInputContainer';
import { StyledTextField } from '../../../core/theme/formComponents';
import ActionButton from '../../../core/components/ui/ActionButton';
import TermsAndCondition from './TermsAndCondition';

interface PhoneInputStepProps {
  activeTab: 'login' | 'signup';
  isSendingCode: boolean;
  onSendVerificationCode: (phoneNumber: string, email?: string) => void;
  emailValidationError?: string;
  emailRequired?: boolean;
  isConsentChecked?: boolean;
}

const PhoneInputStep: React.FC<PhoneInputStepProps> = ({
  activeTab,
  isSendingCode,
  onSendVerificationCode,
  emailValidationError,
  emailRequired = false,
}) => {
  const [telephoneNumber, setTelephoneNumber] = useState<string>('');
  const [formTelephoneNumber, setFormTelephoneNumberState] = useState(false);
  const [email, setEmail] = useState('');
  const [isConsentChecked, setConsentChecked] = useState(false)
  const telephoneValidationErrorText = (telephone: string) =>
    telephone === '' ? 'Phone number cannot be empty' : null;

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  }, []);

  const handleSendCode = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSendVerificationCode(telephoneNumber, activeTab === 'signup' ? email : undefined);
  }, [telephoneNumber, email, activeTab, onSendVerificationCode]);

  const resetState = () => {
    setEmail("");
    setTelephoneNumber("");
    setFormTelephoneNumberState(false);
    setConsentChecked(false)
  }
  useEffect(() => {
    resetState()
  }, [activeTab])

  const handleConsentCheckbox = () => {
    setConsentChecked(!isConsentChecked)
  }
  const isSendDisabled = isSendingCode || !telephoneNumber || (activeTab === 'signup' && emailRequired && !email) || (activeTab === 'signup' && !isConsentChecked);
  return (
    <form onSubmit={handleSendCode}>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Phone Number <span style={{ color: 'red' }}> *</span>
      </Typography>
      <StyledPhoneInputContainer>
        <PhoneInput
          country='us'
          onlyCountries={['us']}
          disableDropdown={true}
          disableCountryCode={true}
          placeholder='(123) 456-7890'
          value={telephoneNumber}
          onChange={(value) => setTelephoneNumber(value)}
          onBlur={() => setFormTelephoneNumberState(true)}
          inputProps={{
            required: true,
            autoFocus: true,
          }}
          enableSearch={true}
          disableSearchIcon={true}
          disabled={isSendingCode}
          autoFormat={true}
          specialLabel=''
          onEnterKeyPress={handleSendCode as any}
        />
      </StyledPhoneInputContainer>

      {formTelephoneNumber && telephoneValidationErrorText(telephoneNumber) && (
        <FormHelperText error required sx={{ marginBottom: '12px' }}>
          {telephoneValidationErrorText(telephoneNumber)}
        </FormHelperText>
      )}

      {activeTab === 'signup' && (
        <>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1, mt: 2 }}>
            {emailRequired ? 'Email' : 'Email (optional)'}
            {emailRequired && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          <StyledTextField
            fullWidth
            id='email'
            placeholder='yourname@example.com'
            variant='outlined'
            value={email}
            onChange={handleEmailChange}
            sx={{ mb: 1 }}
            data-testid='email-input'
            required={emailRequired}
            error={!!emailValidationError}
            helperText={emailValidationError}
          />
          <TermsAndCondition isChecked={isConsentChecked} onCheck={handleConsentCheckbox} />
        </>
      )}

      <Box sx={{ mt: 3 }} />
        <ActionButton
          type='submit'
          text='Send Verification Code'
          loading={isSendingCode}
          disabled={isSendDisabled}
        testId='send-verification-button'
      />
    </form>
  );
};

export default React.memo(PhoneInputStep);
